import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctionsInstance } from '@lib/firebase';

import styles from './StagingPasswordGate.module.css';

interface StagingPasswordGateProps {
    children: React.ReactNode;
}

export const StagingPasswordGate: React.FC<StagingPasswordGateProps> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
        // 1. Always bypass in Development
        if (import.meta.env.DEV) return true;

        // 2. Always bypass if using Emulators
        if (import.meta.env.VITE_USE_EMULATORS === 'true') return true;

        // 3. Enforce in staging or production mode to comply with pre-licensing advertising bans
        const isStaging = import.meta.env.MODE === 'staging' || import.meta.env.VITE_STAGE === 'staging';
        const isProduction = import.meta.env.MODE === 'production' || import.meta.env.PROD;

        if (!isStaging && !isProduction) {
            return true; // Bypass for Test environments, etc.
        }

        // 4. If Staging/Production, check for existing session
        const stored = sessionStorage.getItem('stagingPasswordAuthenticated');
        return stored === 'true';
    });

    const [password, setPassword] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const functions = getFunctionsInstance();
            if (!functions) throw new Error('Firebase functions not initialized');
            const validatePassword = httpsCallable<{ password: string }, { success: boolean }>(functions, 'validateStagingPassword');
            await validatePassword({ password });

            setIsAuthenticated(true);
            sessionStorage.setItem('stagingPasswordAuthenticated', 'true');
            setPassword('');
        } catch (err: any) {
            setError('Invalid password');
            setPassword('');
            console.error('Staging password validation failed:', err.message);
        } finally {
            setLoading(false);
        }
    };

    if (isAuthenticated) {
        return <>{children}</>;
    }

    const isStaging = import.meta.env.MODE === 'staging' || import.meta.env.VITE_STAGE === 'staging';
    const titleText = isStaging ? '🚧 Staging Environment' : '🔑 Auditor Access Gate';
    const descText = isStaging
        ? 'This is a private staging environment for testing. Please enter the password to continue.'
        : 'Fastrack Driving School LMS is currently in pre-licensing review. Public registration is restricted. Authorized auditors and staff, please enter the access password to continue.';

    const envDesc = isStaging 
        ? 'Internal testing & development portal'
        : 'Ohio Department of Public Safety (ODPS) audit portal';

    return (
        <div className={styles.container}>
            <div className={styles.gate}>
                <h1>{titleText}</h1>
                <p>{descText}</p>

                <form className={styles.form} onSubmit={handleSubmit}>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password"
                        autoFocus
                        className={styles.input}
                        disabled={loading}
                    />
                    <button type="submit" className={styles.button} disabled={loading}>
                        {loading ? 'Validating...' : 'Access'}
                    </button>
                </form>

                {error && <p className={styles.error}>{error}</p>}

                <div className={styles.info}>
                    <p>
                        <strong>Environment:</strong> {envDesc}
                    </p>
                    <p>
                        <strong>Support:</strong> If you are an auditor and need assistance, please contact your account representative.
                    </p>
                </div>
            </div>
        </div>
    );
};
